import { useRef } from 'react';
import { useCallback, useEffect } from 'react';
import { listSessions, acknowledgeCompletion } from './api';
import type { ChatSession } from './types';
import { resetInFlightTurnState } from '../chatTurnState';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { PENDING_ID, sessionsEqual } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatHydration } from './useHomeChatHydration';


type Inputs = Pick<HomeChatPanelState, 'chatCards' | 'setWorkflowOnlyPurchaseGuidance' | 'workflowOnlyPurchaseGuidance'> &
Required<Pick<Props, 'constrainedWorkflowEntry' | 'workflowOnly'>> &
Pick<HomeChatPanelState,
  | 'activeSessionIdRef'
  | 'sessionListRefreshVersionRef'
  | 'sessionsRef'
  | 'setSessionsLoading'
  | 'pendingRequestIdRef'
  | 'setActiveSessionId'
  | 'setSessions'
  | 'interactionRevisionRef'
  | 'setInFlight'
  | 'setInFlightUserMsg'
  | 'setInFlightClarification'
  | 'setInFlightTopicFocus'
  | 'stoppingOperationIdRef'
  | 'setStoppingOperationId'
  | 'setReplacingTurnId'
  | 'scrollThreadToLatest'
  | 'reportedSessionIdRef'
  | 'setMessages'
  | 'setTurns'
  | 'setChatCards'
  | 'setAttachments'
  | 'setGpcChatView'
  | 'setMrChatView'
  | 'setConstructionSowChatView'
  | 'setRehydratingGpcSessionId'
  | 'inFlightTitleTimerRef'
  | 'inFlightActivityTimerRef'
  | 'activeSessionId'
  | 'sessions'
> &
Pick<HomeChatHydration,
  | 'notify'
  | 'isStaleResolve'
  | 'hydrateDirectSearchThread'
  | 'clearPendingIfOwned'
> &
Pick<Props, 'onActiveSessionIdChange'> &
Required<Pick<Props, 'initialSessionId'>>;

export function useHomeChatSessionReconciliation({
  activeSessionIdRef,
  sessionListRefreshVersionRef,
  sessionsRef,
  setSessionsLoading,
  pendingRequestIdRef,
  setActiveSessionId,
  setSessions,
  notify,
  isStaleResolve,
  interactionRevisionRef,
  hydrateDirectSearchThread,
  setInFlight,
  clearPendingIfOwned,
  setInFlightUserMsg,
  setInFlightClarification,
  setInFlightTopicFocus,
  stoppingOperationIdRef,
  setStoppingOperationId,
  setReplacingTurnId,
  scrollThreadToLatest,
  reportedSessionIdRef,
  setMessages,
  setTurns,
  setChatCards,
  setAttachments,
  setGpcChatView,
  setMrChatView,
  setConstructionSowChatView,
  setRehydratingGpcSessionId,
  onActiveSessionIdChange,
  inFlightTitleTimerRef,
  inFlightActivityTimerRef,
  activeSessionId,
  initialSessionId,
  sessions,
  setWorkflowOnlyPurchaseGuidance,
  constrainedWorkflowEntry,
  workflowOnly,
}: Inputs) {

  /**
   * Merge the server session list with the active/in-flight entry the
   * client knows about. The server hides zero-turn sessions (orphan
   * suppression in ``list_sessions``), so a brand-new chat that's still
   * mid-flight (no persisted turn yet) — or the client-only ``PENDING_ID``
   * placeholder — would be dropped from the rail on any re-fetch, causing
   * the "New Chat" entry to flash and disappear. We KEEP the active entry
   * visible: if the active session id is absent from the server list, we
   * re-insert its prior rail row (preferred — preserves any title we
   * already had) or synthesize a minimal untitled row. The zero-turn
   * filter then only hides NON-active empty sessions (the true orphan).
   */
  const mergeActiveIntoSessions = useCallback(
    (serverSessions: ChatSession[], prevSessions: ChatSession[]): ChatSession[] => {
      const activeId = activeSessionIdRef.current;
      if (!activeId) return serverSessions;
      if (serverSessions.some((s) => s.session_id === activeId)) {
        // Server already lists the active session (it has a turn now) —
        // trust the server row (carries the freshly-backfilled title).
        return serverSessions;
      }
      // Active session missing from the server list (zero-turn filter or
      // the client-only placeholder). Re-insert it at the top so it stays
      // visible through submit + generation.
      const priorActive = prevSessions.find((s) => s.session_id === activeId);
      const nowIso = new Date().toISOString();
      const activeEntry: ChatSession =
        priorActive ?? {
          session_id: activeId,
          title: null,
          created_at: nowIso,
          last_activity_at: nowIso,
          message_count: 0,
        };
      return [activeEntry, ...serverSessions];
    },
    []
  );

  const refreshSessions = useCallback(async ({
    silently = false,
  }: {
    silently?: boolean;
  } = {}) => {
    const refreshVersion = sessionListRefreshVersionRef.current + 1;
    sessionListRefreshVersionRef.current = refreshVersion;
    // Only surface the loading skeleton on the INITIAL load (when the rail
    // is still empty). Background refreshes — polls, mid-turn title refresh,
    // post-turn / session-switch / delete refetches — must keep the current
    // list visible and reconcile in place. Otherwise every transition flips
    // `sessionsLoading` true, SessionsRail swaps the populated list for the
    // Skeleton, and the rail visibly flashes/blanks then repopulates.
    const isInitialLoad = sessionsRef.current.length === 0;
    if (isInitialLoad) {
      setSessionsLoading(true);
    }
    try {
      const resp = await listSessions();
      if (refreshVersion !== sessionListRefreshVersionRef.current) return;
      let serverSessions = resp.sessions || [];
      // An active conversation is already on screen. Optimistically consume
      // its Ready marker in this very reconciliation pass, then persist that
      // acknowledgement server-side. This covers a live answer that was
      // rendered from the POST response (without waiting for a later reload)
      // while leaving genuinely background sessions untouched.
      const activeReadyCompletion = serverSessions.find((session) => (
        session.session_id === activeSessionIdRef.current
        && session.completion?.state === 'completed'
        && session.completion.unseen
      ));
      if (activeReadyCompletion?.completion) {
        const completion = activeReadyCompletion.completion;
        serverSessions = serverSessions.map((session) => (
          session.session_id === activeReadyCompletion.session_id
            ? { ...session, completion: { ...completion, unseen: false } }
            : session
        ));
        void acknowledgeCompletion(
          activeReadyCompletion.session_id,
          completion.completion_id,
        ).catch(() => {
          // The server remains authoritative. A later list/thread read will
          // retry the idempotent acknowledgement if this best-effort write
          // is interrupted.
        });
      }
      // A lazy first-turn POST cannot return its server session id until the
      // answer finishes. As soon as the server classifies it as ordinary Q&A,
      // its activity envelope safely echoes the browser's opaque request id;
      // adopt that owned session so Stop can address the server-issued
      // operation immediately rather than waiting for the answer response.
      const pendingId = pendingRequestIdRef.current;
      const matchingActiveSession = (
        activeSessionIdRef.current === PENDING_ID
        && pendingId
        ? serverSessions.find((session) => (
          session.activity?.client_request_id === pendingId
        ))
        : undefined
      );
      if (matchingActiveSession) {
        activeSessionIdRef.current = matchingActiveSession.session_id;
        setActiveSessionId(matchingActiveSession.session_id);
      }
      setSessions((prev) => {
        const next = mergeActiveIntoSessions(serverSessions, prev);
        // Reuse the previous reference when nothing actually changed so the
        // rail doesn't re-render (and thus can't flash) on an identical poll.
        return sessionsEqual(prev, next) ? prev : next;
      });
    } catch (e: any) {
      if (!silently && refreshVersion === sessionListRefreshVersionRef.current) {
        notify('error', e?.message || 'Failed to load chats.');
      }
    } finally {
      if (
        isInitialLoad
        && refreshVersion === sessionListRefreshVersionRef.current
      ) {
        setSessionsLoading(false);
      }
    }
  }, [notify, mergeActiveIntoSessions]);

  // The typed constrained route persists its binding/card synchronously, but
  // its response can arrive after the first rail list call.  Reconcile once
  // at the terminal edge so the real server-authorized row replaces the
  // active zero-turn placeholder without waiting for the background poll.
  const constrainedEntryWasPendingRef = useRef(false);
  useEffect(() => {
    if (!workflowOnly) return;
    if (constrainedWorkflowEntry?.state === 'pending') {
      constrainedEntryWasPendingRef.current = true;
      return;
    }
    if (constrainedEntryWasPendingRef.current) {
      constrainedEntryWasPendingRef.current = false;
      void refreshSessions({ silently: true });
    }
  }, [constrainedWorkflowEntry, refreshSessions, workflowOnly]);

  /**
   * Cancellation is a successful, server-authoritative terminal transition,
   * not a failed submission. Rehydrate the status turn that the server wrote
   * so its query is immediately editable, then reconcile the rail before a
   * stale activity snapshot can render a second composer-level spinner.
   */
  const reconcileExpectedCancellation = useCallback(async (requestId?: string) => {
    // A replacement has a distinct browser request id and server operation.
    // An old 409 is informative only while it still owns this pane; after a
    // branch replacement it must never rehydrate its cancelled snapshot over
    // the newer authoritative branch or clear the newer in-flight state.
    if (requestId && isStaleResolve(requestId)) return;
    const sessionId = activeSessionIdRef.current;
    const interactionRevision = interactionRevisionRef.current;
    const stillOwnsCancellation = () => (
      activeSessionIdRef.current === sessionId
      && interactionRevisionRef.current === interactionRevision
      && (!requestId || !isStaleResolve(requestId))
    );
    if (sessionId && sessionId !== PENDING_ID) {
      await hydrateDirectSearchThread(
        sessionId,
        stillOwnsCancellation,
      );
    }
    if (!stillOwnsCancellation()) return;
    await refreshSessions({ silently: true });
    if (!stillOwnsCancellation()) return;
    // The durable stopped turn is the sole in-chat terminal UI. There is no
    // composer-level activity or terminal status to suppress or reconcile.
    // Do this on the cancel receipt rather than waiting for the original
    // long-running POST to eventually reject.  Clearing the request-id mirror
    // also makes that late POST stale, so it cannot repaint this terminal
    // server-owned branch when it finally settles.
    resetInFlightTurnState({
      setInFlight,
      clearPendingRequestId: () => clearPendingIfOwned(requestId),
      setInFlightUserMsg,
      setInFlightClarification,
      setInFlightTopicFocus,
    });
    stoppingOperationIdRef.current = null;
    setStoppingOperationId(null);
    setReplacingTurnId(null);
    scrollThreadToLatest();
  }, [hydrateDirectSearchThread, isStaleResolve, refreshSessions, scrollThreadToLatest, clearPendingIfOwned]);

  const clearUnavailableSession = useCallback((sessionId: string) => {
    if (activeSessionIdRef.current !== sessionId) return;
    activeSessionIdRef.current = null;
    reportedSessionIdRef.current = null;
    setActiveSessionId(null);
    setSessions((current) => current.filter(
      (session) => session.session_id !== sessionId,
    ));
    setMessages([]);
    setTurns([]);
    setChatCards([]);
    setWorkflowOnlyPurchaseGuidance(null);
    setAttachments([]);
    setGpcChatView(null);
    setMrChatView(null);
    setConstructionSowChatView(null);
    setRehydratingGpcSessionId(null);
    onActiveSessionIdChange?.(null);
  }, [onActiveSessionIdChange]);

  /**
   * Schedule a single bounded mid-turn rail refresh so the parallel-
   * generated session title surfaces BEFORE the answer. The backend runs
   * the title agent in parallel with the relevance+clarification gate and
   * persists the title the moment the gate resolves — typically a second or
   * two after submit, well before the reader/synth round-trips complete. We
   * fire ONE delayed `refreshSessions()` (no loop) so the active "New Chat"
   * rail entry fills in early; the existing merge logic keeps it in the same
   * continuously-present row (no flash). Any prior pending fire is cancelled
   * so rapid re-sends don't stack timers.
   */
  const scheduleInFlightTitleRefresh = useCallback(
    (_crid?: string) => {
      if (inFlightTitleTimerRef.current) {
        clearTimeout(inFlightTitleTimerRef.current);
      }
      inFlightTitleTimerRef.current = setTimeout(() => {
        inFlightTitleTimerRef.current = null;
        void refreshSessions({ silently: true });
      }, 1500);
    },
    [refreshSessions]
  );

  const refreshAfterTurnDispatch = useCallback(() => {
    void refreshSessions({ silently: true });
    if (inFlightActivityTimerRef.current) {
      clearTimeout(inFlightActivityTimerRef.current);
    }
    inFlightActivityTimerRef.current = setTimeout(() => {
      inFlightActivityTimerRef.current = null;
      void refreshSessions({ silently: true });
    }, 250);
  }, [refreshSessions]);

  // On mount: load sessions list. Do NOT auto-select.
  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  // Activity belongs to any conversation, not merely the open one. Poll the
  // server-owned summaries while this panel is mounted so a chat that keeps
  // working after the user starts or opens another chat remains visible in
  // the rail. `refreshSessions` preserves an unchanged list reference, so
  // routine poll ticks do not repaint or flash stable rows.
  useEffect(() => {
    const pollId = window.setInterval(() => {
      void refreshSessions({ silently: true });
    }, 3000);
    return () => window.clearInterval(pollId);
  }, [refreshSessions]);

  // Clear any pending in-flight title-refresh timer on unmount so it can't
  // fire a `refreshSessions()` (setState) after the panel is gone.
  useEffect(() => {
    return () => {
      sessionListRefreshVersionRef.current += 1;
      interactionRevisionRef.current += 1;
      activeSessionIdRef.current = null;
      if (inFlightTitleTimerRef.current) {
        clearTimeout(inFlightTitleTimerRef.current);
        inFlightTitleTimerRef.current = null;
      }
      if (inFlightActivityTimerRef.current) {
        clearTimeout(inFlightActivityTimerRef.current);
        inFlightActivityTimerRef.current = null;
      }
    };
  }, []);

  // Keep `activeSessionIdRef` in lockstep with `activeSessionId` so the
  // `refreshSessions` merge always reads the current active id.
  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
    if (activeSessionId === PENDING_ID) return;
    if (reportedSessionIdRef.current === activeSessionId) return;

    reportedSessionIdRef.current = activeSessionId;
    // `initialSessionId` is App's authoritative hydration value. Echoing that
    // value back as a user selection makes App persist stale remembered state
    // and can race with a cross-tab clear. Real user selections differ from
    // the current prop until this callback updates App, so they still report.
    if (activeSessionId !== initialSessionId) {
      onActiveSessionIdChange?.(activeSessionId);
    }
  }, [activeSessionId, initialSessionId, onActiveSessionIdChange]);

  // Keep `sessionsRef` in lockstep with `sessions` so `refreshSessions` can
  // detect the initial-empty-load case without re-creating its identity.
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  return {
    refreshSessions,
    reconcileExpectedCancellation,
    clearUnavailableSession,
    scheduleInFlightTitleRefresh,
    refreshAfterTurnDispatch,
  };
}

export type HomeChatSessionReconciliation = ReturnType<typeof useHomeChatSessionReconciliation>;
